// ═══════════════════════════════════════════════════════════════════════════════
// Super-admin maintenance mode editor (settings tab + break-glass page)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import type { MaintenanceModeAdminView } from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';

type Draft = {
  enabled: boolean;
  messageZhHant: string;
  messageZhHans: string;
  messageEn: string;
  startAt: string;
  endAt: string;
  publicApiEnabled: boolean;
  adminPortalEnabled: boolean;
  cronEnabled: boolean;
};

const toDraft = (view: MaintenanceModeAdminView): Draft => ({
  enabled: view.enabled,
  messageZhHant: view.message['zh-Hant'] ?? '',
  messageZhHans: view.message['zh-Hans'] ?? '',
  messageEn: view.message.en ?? '',
  startAt: view.startAt ?? '',
  endAt: view.endAt ?? '',
  publicApiEnabled: view.publicApiEnabled,
  adminPortalEnabled: view.adminPortalEnabled,
  cronEnabled: view.cronEnabled,
});

type AdminMaintenanceFormProps = {
  /** Compact layout for break-glass page. */
  variant?: 'settings' | 'breakGlass';
  /** Called after a successful save (e.g. refresh public maintenance status). */
  onSaved?: (view: MaintenanceModeAdminView) => void;
};

export const AdminMaintenanceForm = ({
  variant = 'settings',
  onSaved,
}: AdminMaintenanceFormProps) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [active, setActive] = useState(false);
  const [envForceEnabled, setEnvForceEnabled] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await adminApi.get<MaintenanceModeAdminView>('/admin/maintenance');
      if (res.success && res.data) {
        setDraft(toDraft(res.data));
        setActive(res.data.active);
        setEnvForceEnabled(res.data.envForceEnabled);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load maintenance settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = useCallback(async () => {
    if (!draft) {
      return;
    }
    if (draft.enabled && draft.startAt.trim() === '') {
      setError('startAt is required when maintenance is enabled (YYYY-MM-DD hh:mm, HKT)');
      return;
    }
    if (
      draft.startAt.trim() !== '' &&
      draft.endAt.trim() !== '' &&
      draft.endAt.trim() <= draft.startAt.trim()
    ) {
      setError('endAt must be after startAt');
      return;
    }
    if (!draft.adminPortalEnabled) {
      const confirmed = window.confirm(
        'Admin portal will be disabled during maintenance. Unlock via the break-glass path with a super-admin account. Continue?',
      );
      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    setError('');
    setInfo('');
    try {
      const res = await adminApi.put<MaintenanceModeAdminView>('/admin/maintenance', {
        enabled: draft.enabled,
        message: {
          'zh-Hant': draft.messageZhHant,
          'zh-Hans': draft.messageZhHans,
          en: draft.messageEn,
        },
        startAt: draft.startAt.trim() === '' ? null : draft.startAt.trim(),
        endAt: draft.endAt.trim() === '' ? null : draft.endAt.trim(),
        publicApiEnabled: draft.publicApiEnabled,
        adminPortalEnabled: draft.adminPortalEnabled,
        cronEnabled: draft.cronEnabled,
      });
      if (res.success && res.data) {
        setDraft(toDraft(res.data));
        setActive(res.data.active);
        setEnvForceEnabled(res.data.envForceEnabled);
        setInfo('Maintenance settings saved.');
        onSaved?.(res.data);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [draft, onSaved]);

  if (loading || !draft) {
    return <p className="text-sm text-slate-400">Loading maintenance settings…</p>;
  }

  const fieldClass =
    'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100';
  const labelClass = 'block text-xs font-medium text-slate-400';

  return (
    <div className={variant === 'breakGlass' ? 'space-y-4' : 'space-y-4'}>
      <div className="rounded-md border border-slate-700 bg-slate-900/60 p-4">
        <p className="text-sm text-slate-200">
          Status:{' '}
          <span className={active ? 'font-semibold text-amber-300' : 'font-semibold text-emerald-300'}>
            {active ? 'ACTIVE' : 'inactive'}
          </span>
        </p>
        {envForceEnabled ? (
          <p className="mt-2 text-xs text-rose-300">
            .env forceEnabled is ON — DB schedule is overridden until forceEnabled is cleared and the
            API process is restarted.
          </p>
        ) : null}
        <p className="mt-2 text-xs text-slate-500">
          Schedule uses Asia/Hong_Kong. Active when enabled and startAt ≤ now &lt; endAt (endAt
          optional).
        </p>
      </div>

      {error ? (
        <p className="rounded-md border border-rose-800 bg-rose-950/40 px-3 py-2 text-sm text-rose-200" role="alert">
          {error}
        </p>
      ) : null}
      {info ? (
        <p className="rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-200">
          {info}
        </p>
      ) : null}

      <label className="flex items-center gap-2 text-sm text-slate-200">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          className="rounded border-slate-600"
        />
        Enable maintenance mode (also requires schedule window)
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className={labelClass}>Start (HKT, YYYY-MM-DD hh:mm)</span>
          <input
            type="text"
            value={draft.startAt}
            onChange={(e) => setDraft({ ...draft, startAt: e.target.value })}
            className={fieldClass}
            placeholder="2026-07-20 02:00"
            aria-label="Maintenance start datetime HKT"
          />
        </label>
        <label className="block">
          <span className={labelClass}>End (HKT, optional)</span>
          <input
            type="text"
            value={draft.endAt}
            onChange={(e) => setDraft({ ...draft, endAt: e.target.value })}
            className={fieldClass}
            placeholder="2026-07-20 06:00"
            aria-label="Maintenance end datetime HKT"
          />
        </label>
      </div>

      <div className="space-y-3">
        <p className={labelClass}>Message shown in app modal (optional; id/fil use English)</p>
        <label className="block">
          <span className={labelClass}>Traditional Chinese</span>
          <textarea
            value={draft.messageZhHant}
            onChange={(e) => setDraft({ ...draft, messageZhHant: e.target.value })}
            className={fieldClass}
            rows={2}
            aria-label="Maintenance message Traditional Chinese"
          />
        </label>
        <label className="block">
          <span className={labelClass}>Simplified Chinese</span>
          <textarea
            value={draft.messageZhHans}
            onChange={(e) => setDraft({ ...draft, messageZhHans: e.target.value })}
            className={fieldClass}
            rows={2}
            aria-label="Maintenance message Simplified Chinese"
          />
        </label>
        <label className="block">
          <span className={labelClass}>English</span>
          <textarea
            value={draft.messageEn}
            onChange={(e) => setDraft({ ...draft, messageEn: e.target.value })}
            className={fieldClass}
            rows={2}
            aria-label="Maintenance message English"
          />
        </label>
      </div>

      <div className="space-y-2 rounded-md border border-slate-800 p-3">
        <label className="flex items-center gap-2 text-sm text-slate-200">
          <input
            type="checkbox"
            checked={draft.publicApiEnabled}
            onChange={(e) => setDraft({ ...draft, publicApiEnabled: e.target.checked })}
            className="rounded border-slate-600"
          />
          Public APIs during maintenance (pricing / FAQ / legal) — default on
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-200">
          <input
            type="checkbox"
            checked={draft.adminPortalEnabled}
            onChange={(e) => setDraft({ ...draft, adminPortalEnabled: e.target.checked })}
            className="rounded border-slate-600"
          />
          Admin portal during maintenance — default off
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-200">
          <input
            type="checkbox"
            checked={draft.cronEnabled}
            onChange={(e) => setDraft({ ...draft, cronEnabled: e.target.checked })}
            className="rounded border-slate-600"
          />
          Cron jobs during maintenance — default off
        </label>
      </div>

      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={saving}
        className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
        aria-label="Save maintenance settings"
      >
        {saving ? 'Saving…' : 'Save maintenance settings'}
      </button>
    </div>
  );
};
